import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CheckOutlined from "@mui/icons-material/CheckOutlined";
import { useEffect, useState } from "react";
import ButtonIcon from "../../../../../components/icons/PenPaperIcon";
import apiService from "../../../../../service/Api";
import { tokens } from "../../../../../theme";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../../store/Index";

interface ReviewData {
  id: number;
  booking: number;
  contact: number | null;
  client_review: number;
  comment: string;
  improvement_suggestions: string[];
  would_recommend: string;
  recommendation_reason: string;
  created_at: string;
  updated_at: string;
}

interface ReviewResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ReviewData[];
}

interface Props {
  bookingId: string;
}


const RATING_LABELS: Record<number, string> = {
  1: "😡 Very Poor",
  2: "😕 Poor",
  3: "😐 Average",
  4: "😊 Good",
  5: "🤩 Excellent",
};

const formatDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const StarRating = ({ rating }: { rating: number }) => (
  <Stack direction="row" spacing={0.5} justifyContent="center">
    {[1, 2, 3, 4, 5].map((star) => (
      <Box
        key={star}
        component="span"
        sx={{ fontSize: 28, color: star <= rating ? "#f59e0b" : "text.disabled" }}
      >
        ★
      </Box>
    ))}
  </Stack>
);


export default function ClientReviewTab({ bookingId }: Props) {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  const [review, setReview] = useState<ReviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [notes, setNotes] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");

  useEffect(() => {
    if (!bookingId) return;

    const fetchReview = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.get<ReviewResponse>(
          `/api/bookings/booking-review/?booking=${bookingId}`,
        );
        const first = response.data.results?.[0] ?? null;
        setReview(first);
        setNotes("");
        setEditValue("");
      } catch (err) {
        console.error("Failed to fetch review", err);
        setError("Could not load review data.");
      } finally {
        setLoading(false);
      }
    };

    fetchReview();
  }, [bookingId]);

  const handleEditToggle = () => {
    if (isEditing) {
      setNotes(editValue);
    } else {
      setEditValue(notes);
    }
    setIsEditing(!isEditing);
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={6}>
        <CircularProgress size={28} sx={{ color: colors.primary[400] }} />
      </Box>
    );
  }


  if (error) {
    return (
      <Box py={4} textAlign="center">
        <Typography color="error" fontSize={14}>
          {error}
        </Typography>
      </Box>
    );
  }


  if (!review) {
    return (
      <Box py={6} textAlign="center">
        <Typography fontSize={32} mb={1}>
          📭
        </Typography>
        <Typography fontWeight={600} color="text.primary" mb={0.5}>
          No review submitted yet
        </Typography>
        <Typography fontSize={13} color="text.secondary">
          The client hasn&apos;t left a review for this booking.
        </Typography>
      </Box>
    );
  }


  return (
    <Box sx={{ width: "100%", mx: "auto", py: 2 }}>

      {/* Rating card */}
      <Paper
        sx={{
          borderRadius: 3,
          p: 4,
          boxShadow: 0,
          //border: `1px solid ${colors.primary[600]}40`,
          mb: 2,
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            fontSize: "38px",
            fontWeight: 800,
            color: colors.primary[400],
            lineHeight: 1,
            mb: 1,
          }}
        >
          {review.client_review.toFixed(1)}
        </Typography>
        <StarRating rating={review.client_review} />
        <Typography
          sx={{ fontSize: "16px", fontWeight: 600, color: colors.primary[400], mt: 1 }}
        >
          {RATING_LABELS[review.client_review] ?? "Rated"}
        </Typography>
        <Typography sx={{ fontSize: "14px", color: "text.secondary", mt: 0.5 }}>
          {formatDate(review.created_at)}
        </Typography>
      </Paper>

      {/* Feedback card */}
      <Paper
        sx={{
          borderRadius: 3,
          p: 3,
          boxShadow: 0,
          //border: `1px solid ${colors.primary[600]}40`,
          mb: 2,
        }}
      >
        <Typography
          sx={{ fontWeight: 600, fontSize: "15px", color: "text.primary", mb: 2 }}
        >
          Client Feedback
        </Typography>

        {review.comment ? (
          <Typography
            sx={{
              fontSize: "14px",
              color: "text.secondary",
              fontStyle: "italic",
              lineHeight: 1.7,
              mb: 2.5,
            }}
          >
            &quot;{review.comment}&quot;
          </Typography>
        ) : (
          <Typography sx={{ fontSize: "14px", color: "text.disabled", mb: 2.5 }}>
            No comment provided.
          </Typography>
        )}

        {/* Would recommend */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          mb={review.recommendation_reason ? 1.5 : 2}
        >
          <Typography fontSize={13} color="text.secondary">
            Would recommend:
          </Typography>
          <Chip
            label={review.would_recommend || "—"}
            size="small"
            sx={{
              bgcolor: colors.primary[600],
              color: colors.primary[400],
              fontWeight: 600,
              fontSize: "12px",
              borderRadius: 4,
            }}
          />
        </Stack>

        {/* Recommendation reason */}
        {review.recommendation_reason && (
          <Typography
            sx={{
              fontSize: "13px",
              color: "text.secondary",
              fontStyle: "italic",
              lineHeight: 1.6,
              mb: 2,
              pl: 0.5,
            }}
          >
            &quot;{review.recommendation_reason}&quot;
          </Typography>
        )}

        {/* Improvement tags */}
        {review.improvement_suggestions?.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {review.improvement_suggestions.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="medium"
                sx={{
                  bgcolor: colors.primary[600],
                  color: colors.primary[400],
                  fontWeight: 500,
                  fontSize: "12px",
                  borderRadius: 4,
                }}
              />
            ))}
          </Stack>
        )}
      </Paper>

      {/* Internal notes card */}
      <Paper
        sx={{
          borderRadius: 3,
          p: 3,
          boxShadow: 0,
          //border: `1px solid ${colors.primary[600]}40`,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography sx={{ fontWeight: 600, fontSize: "15px", color: "text.primary" }}>
            Internal Notes
          </Typography>
          <IconButton size="small" onClick={handleEditToggle}>
            {isEditing ? (
              <CheckOutlined sx={{ fontSize: 24, color: colors.primary[400] }} />
            ) : (
              <ButtonIcon sx={{ fontSize: 24, color: "text.secondary" }} />
            )}
          </IconButton>
        </Stack>

        {isEditing ? (
          <TextField
            fullWidth
            multiline
            rows={4}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                fontSize: "14px",
                minHeight: "120px",
                "& fieldset": { borderColor: `${colors.primary[300]}40` },
                "&:hover fieldset": { borderColor: colors.primary[300] },
                "&.Mui-focused fieldset": { borderColor: colors.primary[400] },
              },
            }}
          />
        ) : (
          <Box
            sx={{
              bgcolor: colors.primary[800],
              borderRadius: "10px",
              p: 2,
              minHeight: "120px",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                color: notes ? "text.secondary" : "text.disabled",
                lineHeight: 1.7,
              }}
            >
              {notes || "No internal notes added yet."}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}